import { useEffect, useCallback, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import {
  generateRandomNumbers,
  MAX_BOUND_LENGTH,
  MAX_DECIMAL_PLACES,
  MAX_QUANTITY,
  type RandomNumberError,
  type RandomNumberMode,
  type RandomNumberRequest,
} from '@/pages/random-number-generator/random-number-generator'
import { randomNumberGeneratorRoute } from '@/shared/utility-registry'

const UTILITY = randomNumberGeneratorRoute.analyticsKey

const initialRequest: RandomNumberRequest = {
  min: '1',
  max: '100',
  quantity: '1',
  mode: 'integer',
  decimalPlaces: '2',
  unique: false,
}

type CopyStatus = 'idle' | 'copied' | 'error'

interface ValidationState {
  error: RandomNumberError
  invalidFields: Array<keyof RandomNumberRequest>
}

export function RandomNumberGeneratorPage() {
  const [request, setRequest] = useState<RandomNumberRequest>(initialRequest)
  const [results, setResults] = useState<string[]>([])
  const [validation, setValidation] = useState<ValidationState | null>(null)
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle')

  useEffect(() => {
    if (results.length === 0) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    document.getElementById('random-result')?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'nearest',
    })
  }, [results])

  const updateRequest = useCallback((change: Partial<RandomNumberRequest>) => {
    setRequest((current) => ({ ...current, ...change }))
    setResults([])
    setValidation(null)
    setCopyStatus('idle')
  }, [])

  const handleTextInput = useCallback(
    (field: 'min' | 'max' | 'quantity' | 'decimalPlaces') =>
      (event: ChangeEvent<HTMLInputElement>) => updateRequest({ [field]: event.target.value }),
    [updateRequest],
  )

  const handleMode = useCallback((mode: RandomNumberMode) => {
    if (mode === request.mode) return

    updateRequest({ mode })
  }, [request.mode, updateRequest])

  const generate = useCallback(() => {
    const startMs = performance.now()
    toolInvoke(UTILITY, {
      mode: request.mode,
      unique: request.unique,
    })

    const generation = generateRandomNumbers(request)
    if (generation.status === 'error') {
      setResults([])
      setValidation(generation)
      toolError(UTILITY, generation.error, {
        mode: request.mode,
        unique: request.unique,
      })
      return
    }

    setResults(generation.values)
    setValidation(null)
    setCopyStatus('idle')
    toolSuccess(UTILITY, Math.round(performance.now() - startMs), {
      mode: request.mode,
      unique: request.unique,
      quantity: generation.values.length,
    })
  }, [request])

  const copyResults = useCallback(async () => {
    if (results.length === 0 || !navigator.clipboard) {
      setCopyStatus('error')
      return
    }

    try {
      await navigator.clipboard.writeText(results.join('\n'))
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
  }, [results])

  const invalid = useCallback(
    (field: keyof RandomNumberRequest) => validation?.invalidFields.includes(field) ?? false,
    [validation],
  )

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-teal-600 via-amber-400 to-red-500" />
        <div className="grid min-w-0 gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)]">
          <div className="min-w-0 space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <div className="grid grid-cols-2 gap-2">
              <ModeButton active={request.mode === 'integer'} onClick={() => handleMode('integer')}>
                Целые
              </ModeButton>
              <ModeButton active={request.mode === 'decimal'} onClick={() => handleMode('decimal')}>
                Десятичные
              </ModeButton>
            </div>

            <div className="grid min-w-0 grid-cols-2 gap-3 sm:gap-4">
              <NumberField
                id="random-min"
                label="Минимум"
                value={request.min}
                maxLength={MAX_BOUND_LENGTH}
                onChange={handleTextInput('min')}
                invalid={invalid('min')}
              />
              <NumberField
                id="random-max"
                label="Максимум"
                value={request.max}
                maxLength={MAX_BOUND_LENGTH}
                onChange={handleTextInput('max')}
                invalid={invalid('max')}
              />
              <NumberField
                id="random-quantity"
                label={`Количество (до ${MAX_QUANTITY})`}
                value={request.quantity}
                inputMode="numeric"
                onChange={handleTextInput('quantity')}
                invalid={invalid('quantity')}
              />
              {request.mode === 'decimal' && (
                <NumberField
                  id="random-precision"
                  label={`Знаков после точки (0–${MAX_DECIMAL_PLACES})`}
                  value={request.decimalPlaces}
                  inputMode="numeric"
                  onChange={handleTextInput('decimalPlaces')}
                  invalid={invalid('decimalPlaces')}
                />
              )}
            </div>

            <label className={cn(
              'flex min-w-0 cursor-pointer items-start gap-3 rounded-md border bg-background p-4',
              invalid('unique') && 'border-destructive bg-destructive/5',
            )}
            >
              <input
                type="checkbox"
                checked={request.unique}
                onChange={(event) => updateRequest({ unique: event.target.checked })}
                aria-invalid={invalid('unique')}
                aria-describedby={invalid('unique') ? 'random-number-error' : undefined}
                className="mt-0.5 size-4 flex-none accent-primary"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">Только уникальные числа</span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                  Количество не должно превышать число доступных значений в диапазоне.
                </span>
              </span>
            </label>

            {validation && (
              <div id="random-number-error" role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errorText(validation.error)}
              </div>
            )}

            <Button className="w-full sm:w-auto" onClick={generate}>Сгенерировать</Button>
          </div>

          <ResultPanel results={results} copyStatus={copyStatus} onCopy={copyResults} />
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard title="Локально" text="Числа генерируются средствами браузера и не отправляются на сервер." />
        <InfoCard title="Без повторов" text="Unique-режим заранее проверяет, достаточно ли значений в диапазоне." />
        <InfoCard title="Честные ограничения" text="Инструмент не предназначен для ключей, лотерей или воспроизводимых выборок." />
      </section>
    </div>
  )
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'min-w-0 rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
          : 'bg-background hover:border-primary/40 hover:bg-accent/60',
      )}
    >
      {children}
    </button>
  )
}

function NumberField({
  id,
  label,
  value,
  maxLength,
  inputMode = 'decimal',
  onChange,
  invalid,
}: {
  id: string
  label: string
  value: string
  maxLength?: number
  inputMode?: 'decimal' | 'numeric'
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  invalid: boolean
}) {
  return (
    <label htmlFor={id} className="block min-w-0">
      <span className="mb-2 block text-sm font-medium">{label}</span>
      <input
        id={id}
        value={value}
        maxLength={maxLength}
        inputMode={inputMode}
        onChange={onChange}
        aria-invalid={invalid}
        aria-describedby={invalid ? 'random-number-error' : undefined}
        className={cn(
          'h-11 w-full min-w-0 rounded-md border bg-background px-3 font-mono text-base tabular-nums outline-none transition-colors',
          'focus:border-primary focus:ring-2 focus:ring-ring/20',
          invalid && 'border-destructive focus:border-destructive focus:ring-destructive/20',
        )}
      />
    </label>
  )
}

function ResultPanel({
  results,
  copyStatus,
  onCopy,
}: {
  results: string[]
  copyStatus: CopyStatus
  onCopy: () => void
}) {
  return (
    <div id="random-result" className="flex min-w-0 scroll-mt-20 flex-col bg-background/55 p-4 sm:p-6 lg:min-h-[360px]" aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-muted-foreground">Результат</div>
        <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
          {results.length > 0 ? `${results.length} чисел` : 'Нет выборки'}
        </span>
      </div>

      {results.length > 0 ? (
        <>
          <div className="mt-4 max-h-[420px] min-w-0 overflow-auto rounded-md border border-primary/35 bg-card p-4 shadow-sm">
            <ol className="space-y-1 font-mono text-base tabular-nums">
              {results.map((value, index) => (
                <li key={`${index}-${value}`} className="break-all">{value}</li>
              ))}
            </ol>
          </div>
          <Button variant="outline" className="mt-4 w-full sm:w-auto" onClick={onCopy}>
            Скопировать список
          </Button>
          {copyStatus === 'copied' && <p className="mt-2 text-sm text-primary">Список скопирован.</p>}
          {copyStatus === 'error' && <p className="mt-2 text-sm text-destructive">Не удалось скопировать. Выделите значения вручную.</p>}
        </>
      ) : (
        <div className="mt-4 rounded-md border bg-card p-4 shadow-sm">
          <div className="font-semibold">Настройте диапазон</div>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Результаты появятся после команды «Сгенерировать» и сохранятся до изменения параметров.
          </p>
        </div>
      )}
    </div>
  )
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="pt-6">
        <div className="font-semibold">{title}</div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
      </CardContent>
    </Card>
  )
}

function errorText(error: RandomNumberError): string {
  const messages: Record<RandomNumberError, string> = {
    empty_input: 'Заполните все обязательные поля.',
    invalid_number: 'Введите корректные границы диапазона. В целочисленном режиме дробные границы недоступны.',
    invalid_range: 'В диапазоне нет доступных значений: минимум должен быть не больше максимума.',
    invalid_quantity: `Количество должно быть целым числом от 1 до ${MAX_QUANTITY}.`,
    invalid_precision: `Число знаков после точки должно быть целым от 0 до ${MAX_DECIMAL_PLACES}.`,
    unique_count_exceeds_capacity: 'Для уникальной выборки в этом диапазоне недостаточно доступных значений.',
    unsafe_range: 'Диапазон слишком велик для точного расчёта в браузере. Уменьшите границы или точность.',
    processing_failed: 'Не удалось получить случайные данные в браузере. Проверьте настройки браузера и попробуйте ещё раз.',
  }

  return messages[error]
}
